import { useEffect, useRef } from "react";
import { pointerBus } from "./pointerBus";

/**
 * The unlocked site-wide drone cursor (Free's rule: earned past 5 lifetime
 * laps). A tiny quad rides the pointer with a touch of lag and banks into
 * its heading. Pointer-events none; the real cursor hides via body class.
 */
export function DroneCursor({ active }: { active: boolean }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active) return;
    document.body.classList.add("drone-cursor-on");
    const el = ref.current;
    if (!el) return;
    let x = window.innerWidth / 2, y = window.innerHeight / 2;
    let tx = x, ty = y;
    let heading = 0;
    const onMove = (e: PointerEvent) => {
      tx = e.clientX;
      ty = e.clientY;
    };
    window.addEventListener("pointermove", onMove);
    let raf = 0;
    let last = performance.now();
    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      const dt = Math.min(0.05, Math.max(0.001, (now - last) / 1000));
      last = now;
      const px = x, py = y;
      const k = Math.min(1, dt * 22);
      x += (tx - x) * k;
      y += (ty - y) * k;
      const vx = (x - px) / dt, vy = (y - py) / dt;
      if (Math.hypot(vx, vy) > 50) {
        const want = Math.atan2(vy, vx);
        let d = want - heading;
        while (d > Math.PI) d -= Math.PI * 2;
        while (d < -Math.PI) d += Math.PI * 2;
        heading += d * 0.2;
      }
      el.style.transform = "translate(" + (x - 13) + "px," + (y - 13) + "px) rotate(" + heading + "rad)";
      // publish the LAGGED position: the swarm should dodge the drone you
      // can see, not the pointer it is chasing
      pointerBus.x = x;
      pointerBus.y = y;
      pointerBus.active = true;
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      document.body.classList.remove("drone-cursor-on");
      pointerBus.active = false;
    };
  }, [active]);

  if (!active) return null;
  return (
    <div ref={ref} className="drone-cursor" aria-hidden="true">
      <svg viewBox="0 0 26 26" width="26" height="26">
        <g stroke="#39404f" strokeWidth="2.6" strokeLinecap="round">
          <line x1="6" y1="6" x2="20" y2="20" />
          <line x1="20" y1="6" x2="6" y2="20" />
        </g>
        <circle cx="20" cy="6" r="3" fill="#ffb020" />
        <circle cx="20" cy="20" r="3" fill="#ffb020" />
        <circle cx="6" cy="6" r="3" fill="#ff4545" />
        <circle cx="6" cy="20" r="3" fill="#ff4545" />
        <circle cx="13" cy="13" r="2.2" fill="#1d222d" />
      </svg>
    </div>
  );
}
